/**
 * Procedural, generative MUSIC for The Paradox Engine.
 * Pure Web Audio API — no asset files.
 *
 * Each act has a real musical identity: key/scale, a looping chord
 * progression, a recurring melodic theme (motif), arpeggios, bass, and —
 * from Act III on — soft percussion. A look-ahead scheduler (the classic
 * "Tale of Two Clocks" pattern) keeps rhythm tight against the audio clock.
 *
 *   Act I    — A minor, warm & wandering. Pad + bass + sparse theme.
 *   Act II   — adds arpeggios and a yearning V chord (harmonic-minor color).
 *   Act III  — "the Engine": faster, soft click track joins, theme drives.
 *   Act IV   — "the Optimizer": darker, diminished color, sub-bass + heavier beat.
 *   endgame  — sparse, slow: pad and an occasional far-off note.
 *
 * Transitions apply at the next bar boundary; layer buses crossfade.
 */

type Act = 1 | 2 | 3 | 4 | 'endgame';

const VOLUME_KEY = 'tpe.audioVolume';
const MUTE_KEY = 'tpe.audioMuted';

type ChordQuality = 'min' | 'maj' | 'dim';
interface Chord {
  root: number; // MIDI note of the chord root
  q: ChordQuality;
}
type LayerName = 'pad' | 'mel' | 'bass' | 'arp' | 'perc' | 'sub';
interface ActConfig {
  bpm: number;
  prog: Chord[];
  /** scale degrees (semitone offsets) used to map melody degrees → pitch */
  scale: number[];
  /** MIDI note that melody degree 0 maps to */
  melBase: number;
  /** semitone offset from chord root for the bass voice */
  bassOffset: number;
  /** recurring theme: notes placed on the 16-step bar grid, by scale degree */
  motif: { s: number; d: number }[];
  /** play the motif once every N bars */
  motifEveryBars: number;
  layers: Record<LayerName, boolean>;
}

/** Base bus mix levels (pre-master). Tuned to avoid clipping at master 1.0. */
const BUS_LEVEL: Record<LayerName, number> = {
  pad: 0.2,
  mel: 0.26,
  bass: 0.24,
  arp: 0.14,
  perc: 0.9,
  sub: 0.55,
};

const STEPS_PER_BAR = 16; // 4 beats × 4 sixteenths

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let musicGain: GainNode | null = null;
let reverbInput: GainNode | null = null;
const buses: Partial<Record<LayerName, GainNode>> = {};
let noiseBuf: AudioBuffer | null = null;

let schedulerTimer: ReturnType<typeof setInterval> | 0 = 0;
let nextNoteTime = 0;
let currentStep = 0;
let barCount = 0;
let arpIndex = 0;

let cfg: ActConfig | null = null;
let pendingCfg: ActConfig | null = null;
let currentAct: Act | null = null;
let initialized = false;
let muted = false;

// ── persistence / public volume API ────────────────────────────────────
function readVolume(): number {
  const v = parseFloat(localStorage.getItem(VOLUME_KEY) || '0.35');
  return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0.35;
}
function readMuted(): boolean {
  return localStorage.getItem(MUTE_KEY) === '1';
}

export function isMuted(): boolean {
  return muted;
}
export function getVolume(): number {
  return readVolume();
}
export function setVolume(v: number): void {
  const clamped = Math.max(0, Math.min(1, v));
  localStorage.setItem(VOLUME_KEY, String(clamped));
  if (masterGain && ctx && !muted) {
    masterGain.gain.cancelScheduledValues(ctx.currentTime);
    masterGain.gain.linearRampToValueAtTime(clamped, ctx.currentTime + 0.05);
  }
}
export function setMuted(next: boolean): void {
  muted = next;
  localStorage.setItem(MUTE_KEY, next ? '1' : '0');
  if (masterGain && ctx) {
    masterGain.gain.cancelScheduledValues(ctx.currentTime);
    masterGain.gain.linearRampToValueAtTime(
      next ? 0 : readVolume(),
      ctx.currentTime + 0.15,
    );
  }
}

// ── theory helpers ──────────────────────────────────────────────────────
function midiToFreq(m: number): number {
  return 440 * Math.pow(2, (m - 69) / 12);
}
function triad({ root, q }: Chord): number[] {
  if (q === 'maj') return [root, root + 4, root + 7];
  if (q === 'dim') return [root, root + 3, root + 6];
  return [root, root + 3, root + 7]; // min
}
function degreeToMidi(scale: number[], melBase: number, d: number): number {
  const len = scale.length;
  const oct = Math.floor(d / len);
  const pc = scale[((d % len) + len) % len];
  return melBase + oct * 12 + pc;
}

// A natural minor and Dorian degree sets (semitone offsets within an octave).
const AEOLIAN = [0, 2, 3, 5, 7, 8, 10];
const DORIAN = [0, 2, 3, 5, 7, 9, 10];

// Root MIDI references (A2=45, A3=57, A4=69).
function configFor(act: Act): ActConfig {
  switch (act) {
    case 1:
      return {
        bpm: 64,
        scale: DORIAN,
        melBase: 69, // A4
        bassOffset: -12,
        prog: [
          { root: 57, q: 'min' }, // Am
          { root: 53, q: 'maj' }, // F
          { root: 60, q: 'maj' }, // C
          { root: 55, q: 'maj' }, // G
        ],
        motif: [
          { s: 0, d: 4 },
          { s: 6, d: 3 },
          { s: 8, d: 2 },
          { s: 12, d: 4 },
          { s: 14, d: 5 },
        ],
        motifEveryBars: 2,
        layers: { pad: true, mel: true, bass: true, arp: false, perc: false, sub: false },
      };
    case 2:
      return {
        bpm: 72,
        scale: AEOLIAN,
        melBase: 69,
        bassOffset: -12,
        prog: [
          { root: 57, q: 'min' }, // Am
          { root: 55, q: 'maj' }, // G
          { root: 53, q: 'maj' }, // F
          { root: 52, q: 'maj' }, // E (V — harmonic-minor tension)
        ],
        motif: [
          { s: 0, d: 4 },
          { s: 2, d: 5 },
          { s: 4, d: 4 },
          { s: 8, d: 2 },
          { s: 10, d: 3 },
          { s: 12, d: 2 },
          { s: 14, d: 1 },
        ],
        motifEveryBars: 1,
        layers: { pad: true, mel: true, bass: true, arp: true, perc: false, sub: false },
      };
    case 3:
      return {
        bpm: 88,
        scale: AEOLIAN,
        melBase: 69,
        bassOffset: -12,
        prog: [
          { root: 57, q: 'min' }, // Am
          { root: 50, q: 'min' }, // Dm (iv)
          { root: 52, q: 'maj' }, // E  (V)
          { root: 57, q: 'min' }, // Am
        ],
        motif: [
          { s: 0, d: 0 },
          { s: 2, d: 2 },
          { s: 4, d: 4 },
          { s: 6, d: 2 },
          { s: 8, d: 7 },
          { s: 10, d: 4 },
          { s: 12, d: 2 },
          { s: 14, d: 4 },
        ],
        motifEveryBars: 1,
        layers: { pad: true, mel: true, bass: true, arp: true, perc: true, sub: false },
      };
    case 4:
      return {
        bpm: 96,
        scale: AEOLIAN,
        melBase: 69,
        bassOffset: -24,
        prog: [
          { root: 57, q: 'min' }, // Am
          { root: 59, q: 'dim' }, // B° (ii° — bite)
          { root: 53, q: 'maj' }, // F
          { root: 52, q: 'maj' }, // E
        ],
        motif: [
          { s: 0, d: 0 },
          { s: 1, d: 1 },
          { s: 4, d: 3 },
          { s: 6, d: 6 },
          { s: 8, d: 0 },
          { s: 10, d: 4 },
          { s: 12, d: 5 },
          { s: 13, d: 4 },
          { s: 14, d: 3 },
        ],
        motifEveryBars: 1,
        layers: { pad: true, mel: true, bass: true, arp: true, perc: true, sub: true },
      };
    case 'endgame':
    default:
      return {
        bpm: 50,
        scale: AEOLIAN,
        melBase: 69,
        bassOffset: -12,
        prog: [
          { root: 57, q: 'min' }, // Am
          { root: 53, q: 'maj' }, // F
        ],
        motif: [
          { s: 0, d: 0 },
          { s: 8, d: 2 },
        ],
        motifEveryBars: 2,
        layers: { pad: true, mel: true, bass: true, arp: false, perc: false, sub: true },
      };
  }
}

// ── audio graph setup ───────────────────────────────────────────────────
function makeNoiseBuffer(c: AudioContext): AudioBuffer {
  const len = Math.floor(c.sampleRate * 1);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

/** Generate a soft decaying-noise impulse response for the reverb. */
function makeImpulse(c: AudioContext, seconds: number, decay: number): AudioBuffer {
  const rate = c.sampleRate;
  const len = Math.floor(rate * seconds);
  const buf = c.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
  }
  return buf;
}

function ensureCtx(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const Ctx = (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext) as typeof AudioContext;
    ctx = new Ctx();

    masterGain = ctx.createGain();
    masterGain.gain.value = muted ? 0 : readVolume();
    masterGain.connect(ctx.destination);

    musicGain = ctx.createGain();
    musicGain.gain.value = 1;
    musicGain.connect(masterGain);

    // Reverb: input → convolver → return → music bus.
    reverbInput = ctx.createGain();
    reverbInput.gain.value = 0.34;
    const convolver = ctx.createConvolver();
    convolver.buffer = makeImpulse(ctx, 2.6, 2.2);
    const reverbReturn = ctx.createGain();
    reverbReturn.gain.value = 0.9;
    reverbInput.connect(convolver);
    convolver.connect(reverbReturn);
    reverbReturn.connect(musicGain);

    // Per-layer buses → music bus (dry); tonal layers also feed the reverb.
    (Object.keys(BUS_LEVEL) as LayerName[]).forEach((name) => {
      const g = ctx!.createGain();
      g.gain.value = 0;
      g.connect(musicGain!);
      if (name === 'pad' || name === 'mel' || name === 'arp') {
        g.connect(reverbInput!);
      }
      buses[name] = g;
    });

    noiseBuf = makeNoiseBuffer(ctx);
    return ctx;
  } catch {
    return null;
  }
}

function fade(param: AudioParam, target: number, sec: number): void {
  if (!ctx) return;
  param.cancelScheduledValues(ctx.currentTime);
  param.setValueAtTime(param.value, ctx.currentTime);
  param.linearRampToValueAtTime(target, ctx.currentTime + sec);
}

function applyBusTargets(c: ActConfig): void {
  (Object.keys(BUS_LEVEL) as LayerName[]).forEach((name) => {
    const bus = buses[name];
    if (!bus) return;
    fade(bus.gain, c.layers[name] ? BUS_LEVEL[name] : 0, 1.6);
  });
}

// ── voices ──────────────────────────────────────────────────────────────
function envGain(t: number, attack: number, peak: number, release: number): GainNode {
  const c = ctx!;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + release);
  return g;
}

function playPluck(time: number, midi: number, dur: number, level: number, pan = 0): void {
  const c = ctx!;
  const out = buses.mel!;
  const g = envGain(time, 0.008, level, dur);
  const lp = c.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(2600, time);
  lp.frequency.exponentialRampToValueAtTime(900, time + dur);
  const panner = c.createStereoPanner();
  panner.pan.value = pan;

  const osc = c.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = midiToFreq(midi);
  const shimmer = c.createOscillator();
  shimmer.type = 'sine';
  shimmer.frequency.value = midiToFreq(midi + 12);
  const shimGain = c.createGain();
  shimGain.gain.value = 0.25;

  osc.connect(g);
  shimmer.connect(shimGain).connect(g);
  g.connect(lp).connect(panner).connect(out);
  osc.start(time);
  shimmer.start(time);
  osc.stop(time + dur + 0.05);
  shimmer.stop(time + dur + 0.05);
}

function playArp(time: number, midi: number, dur: number): void {
  const c = ctx!;
  const out = buses.arp!;
  const g = envGain(time, 0.006, 0.5, dur);
  const osc = c.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = midiToFreq(midi);
  osc.connect(g).connect(out);
  osc.start(time);
  osc.stop(time + dur + 0.05);
}

function playPad(time: number, midis: number[], dur: number): void {
  const c = ctx!;
  const out = buses.pad!;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, time);
  g.gain.linearRampToValueAtTime(0.5, time + dur * 0.35);
  g.gain.setValueAtTime(0.5, time + dur * 0.7);
  g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
  const lp = c.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 1300;
  lp.Q.value = 0.6;
  g.connect(lp).connect(out);

  for (const m of midis) {
    for (const det of [-5, 5]) {
      const osc = c.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = midiToFreq(m);
      osc.detune.value = det;
      const v = c.createGain();
      v.gain.value = 0.16;
      osc.connect(v).connect(g);
      osc.start(time);
      osc.stop(time + dur + 0.1);
    }
  }
}

function playBass(time: number, midi: number, dur: number): void {
  const c = ctx!;
  const out = buses.bass!;
  const g = envGain(time, 0.012, 0.7, dur);
  const lp = c.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 380;
  const osc = c.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = midiToFreq(midi);
  osc.connect(g).connect(lp).connect(out);
  osc.start(time);
  osc.stop(time + dur + 0.05);
}

function playSub(time: number, midi: number, dur: number): void {
  const c = ctx!;
  const out = buses.sub!;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, time);
  g.gain.exponentialRampToValueAtTime(0.6, time + 0.08);
  g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = midiToFreq(midi);
  osc.connect(g).connect(out);
  osc.start(time);
  osc.stop(time + dur + 0.05);
}

function playKick(time: number): void {
  const c = ctx!;
  const out = buses.perc!;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, time);
  g.gain.exponentialRampToValueAtTime(0.7, time + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, time + 0.18);
  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(135, time);
  osc.frequency.exponentialRampToValueAtTime(45, time + 0.12);
  osc.connect(g).connect(out);
  osc.start(time);
  osc.stop(time + 0.2);
}

function playHat(time: number, level: number): void {
  const c = ctx!;
  if (!noiseBuf) return;
  const out = buses.perc!;
  const src = c.createBufferSource();
  src.buffer = noiseBuf;
  const bp = c.createBiquadFilter();
  bp.type = 'highpass';
  bp.frequency.value = 7000;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, time);
  g.gain.exponentialRampToValueAtTime(level, time + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, time + 0.05);
  src.connect(bp).connect(g).connect(out);
  src.start(time);
  src.stop(time + 0.06);
}

function playClap(time: number): void {
  const c = ctx!;
  if (!noiseBuf) return;
  const out = buses.perc!;
  const src = c.createBufferSource();
  src.buffer = noiseBuf;
  const bp = c.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 1800;
  bp.Q.value = 0.8;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, time);
  g.gain.exponentialRampToValueAtTime(0.4, time + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, time + 0.13);
  src.connect(bp).connect(g).connect(out);
  src.start(time);
  src.stop(time + 0.15);
}

// ── sequencer ─────────────────────────────────────────────────────────────
function scheduleStep(step: number, time: number): void {
  if (!cfg) return;
  const c = cfg;
  const secPerBeat = 60 / c.bpm;
  const stepDur = secPerBeat / 4;
  const chord = c.prog[barCount % c.prog.length];
  const notes = triad(chord);

  // Pad + bass anchor the bar.
  if (step === 0) {
    if (c.layers.pad) playPad(time, notes, stepDur * STEPS_PER_BAR);
    if (c.layers.bass) playBass(time, chord.root + c.bassOffset, secPerBeat * 1.6);
    if (c.layers.sub) playSub(time, chord.root + c.bassOffset - 12, secPerBeat * 3.4);
  }
  // Bass also leans on beat 3.
  if (step === 8 && c.layers.bass) {
    playBass(time, chord.root + c.bassOffset + 7, secPerBeat * 1.2);
  }

  // Arpeggio: one chord tone per eighth, climbing across two octaves.
  if (c.layers.arp && step % 2 === 0) {
    const ladder = [notes[0], notes[1], notes[2], notes[0] + 12, notes[2], notes[1] + 12];
    const m = ladder[arpIndex % ladder.length] + 12;
    arpIndex++;
    playArp(time, m, stepDur * 1.8);
  }

  // Recurring melodic theme.
  if (c.layers.mel && barCount % c.motifEveryBars === 0) {
    for (const note of c.motif) {
      if (note.s !== step) continue;
      // Small chance to leave a note out, for breath (less in the busy acts).
      const skip = currentAct === 1 || currentAct === 'endgame' ? 0.18 : 0.08;
      if (Math.random() < skip) continue;
      const midi = degreeToMidi(c.scale, c.melBase, note.d);
      const pan = (Math.random() - 0.5) * 0.5;
      playPluck(time, midi, secPerBeat * 0.9, 0.5, pan);
    }
  }

  // Percussion (Act III+).
  if (c.layers.perc) {
    if (step === 0 || step === 8) playKick(time);
    if (step % 4 === 2) playHat(time, 0.22);
    if (currentAct === 4 && (step === 4 || step === 12)) playClap(time);
  }
}

function scheduler(): void {
  if (!ctx || !cfg) return;
  const lookahead = document.hidden ? 1.6 : 0.22;
  while (nextNoteTime < ctx.currentTime + lookahead) {
    // Apply a queued act change exactly on a downbeat.
    if (currentStep === 0 && pendingCfg) {
      cfg = pendingCfg;
      pendingCfg = null;
      applyBusTargets(cfg);
    }
    if (!muted) scheduleStep(currentStep, nextNoteTime);

    const secPerBeat = 60 / cfg.bpm;
    nextNoteTime += secPerBeat / 4;
    currentStep = (currentStep + 1) % STEPS_PER_BAR;
    if (currentStep === 0) barCount++;
  }
}

// ── lifecycle ─────────────────────────────────────────────────────────────
/** Initialize audio (must be called from a user gesture). */
export function initAudio(): void {
  if (initialized) return;
  muted = readMuted();
  const c = ensureCtx();
  if (!c) return;
  if (c.state === 'suspended') void c.resume();

  cfg = configFor(currentAct ?? 1);
  applyBusTargets(cfg);
  currentStep = 0;
  barCount = 0;
  arpIndex = 0;
  nextNoteTime = c.currentTime + 0.15;
  if (schedulerTimer) clearInterval(schedulerTimer);
  schedulerTimer = setInterval(scheduler, 40);
  initialized = true;
}

/** Notify audio of the current act; init if needed. Safe to call repeatedly. */
export function setAct(act: Act): void {
  if (act === currentAct) return;
  currentAct = act;
  if (!initialized) return; // wait for a gesture to wake the context
  // Queue for the next downbeat so transitions land musically.
  pendingCfg = configFor(act);
}

// ── one-shot UI sounds ──────────────────────────────────────────────────
/** Short click chime (UI feedback). */
export function clickSound(): void {
  if (!initialized || muted || !ctx || !masterGain) return;
  const c = ctx;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'triangle';
  osc.frequency.value = 880;
  gain.gain.value = 0;
  gain.gain.linearRampToValueAtTime(0.08, c.currentTime + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.18);
  osc.connect(gain).connect(masterGain);
  osc.start();
  osc.stop(c.currentTime + 0.2);
}

/** Soft bell for narrator beats. */
export function bellSound(): void {
  if (!initialized || muted || !ctx || !masterGain) return;
  const c = ctx;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.value = 660;
  gain.gain.value = 0;
  gain.gain.linearRampToValueAtTime(0.12, c.currentTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 1.5);
  osc.connect(gain).connect(masterGain);
  osc.start();
  osc.stop(c.currentTime + 1.6);
}
